import type {Ref} from 'vue';
import {getCurrentInstance, onBeforeUnmount, ref, onMounted, onScopeDispose} from 'vue';
import {FetchError, type NotificationsResource} from '@navbar-api';
import {NavbarContext} from '@navbar-registry';
import {defineStore} from 'pinia';
import {useLogger, useMaltEnvironnement} from '#imports';
import type {MessageFromBrowserInstance, MessageFromWorker} from './notifications.worker';
import sharedWorkerSseUrl from './shared-worker-sse.ts?worker&url';

const EVERY_MINUTE = 60000;

/**
 * On deployed environment, apps share one canonical SharedWorker URL so a single
 * worker instance is reused across front apps.
 * Must not apply on local environments where the file is served by the app itself.
 */
function useResolveSharedWorkerUrl(): URL {
    const workerRelativePath = new URL(sharedWorkerSseUrl, window.location.origin).pathname;
    const environment = useMaltEnvironnement();

    if (environment === 'DEV_INTEGRATION' || environment === 'DEV') {
        return new URL(workerRelativePath, window.location.origin);
    }

    const filename = workerRelativePath.split('/').pop();
    return new URL(`/nuxt_assets/shared-worker-sse/${filename}`, window.location.origin);
}

function useSetupSharedWorkerSse(notificationsRecord: Ref<NotificationsResource | undefined>) {
    // SharedWorkers must be same-origin.
    // The domain of the URL constructed by Vite at build time (with the worker?url) is modified to use the origin.
    // The file is then served from the Origin via Traefik proxy.
    const workerUrl = useResolveSharedWorkerUrl();
    // Compability with Safari 15.6 is checked
    // eslint-disable-next-line compat/compat
    const worker = new SharedWorker(workerUrl, {type: 'module', name: 'sse-notifications-worker'});
    const logger = useLogger();

    function postMessage(message: MessageFromBrowserInstance) {
        if (typeof message === 'object' && !message.timestamp) {
            // useful for debugging
            message.timestamp = new Date().getTime();
        }
        worker.port.postMessage(message);
    }

    worker.port.addEventListener('message', (e: MessageEvent<MessageFromWorker>) => {
        // Apparently, MessageEvent.origin is empty is used when the origin is the same as the current script
        if (e.origin !== '' && e.origin !== window.location.origin) {
            // There's no reason why we would receive a message from another origin than that of the worker,
            // which we spawned ourselves from the same origin.
            logger.error(`Message received from unexpected origin: ${e.origin}`);
            return;
        }

        // eslint-disable-next-line no-console
        console.debug(`Received from worker: ${JSON.stringify(e.data)}`);

        if (e.data.type === 'notifications-update') {
            notificationsRecord.value = e.data.notifications;
        } else {
            logger.error(`Unexpected message: ${JSON.stringify(e.data)}`);
        }
    });

    function publishCloseMessage() {
        postMessage({type: 'before-port-close'});
    }

    window.addEventListener('beforeunload', publishCloseMessage);
    worker.port.start();

    return {
        close() {
            publishCloseMessage();
            worker.port.close();
        },
        requestNotificationsUpdate() {
            postMessage({type: 'notifications-update-request'});
        },
        updateSubscription(active: boolean) {
            postMessage({type: 'subscription', active});
        },
    };
}

function useSseSharedWorker(notificationsRecord: Ref<NotificationsResource | undefined>) {
    const worker = useSetupSharedWorkerSse(notificationsRecord);

    function requestNotificationsUpdate() {
        worker.requestNotificationsUpdate();
    }

    function updateSubscriptionDependingOnDocumentVisibility() {
        const documentVisible = document.visibilityState === 'visible';
        worker.updateSubscription(documentVisible);
    }

    if (getCurrentInstance()) {
        onMounted(() => {
            updateSubscriptionDependingOnDocumentVisibility();
            document.addEventListener('visibilitychange', updateSubscriptionDependingOnDocumentVisibility);
        });
        onBeforeUnmount(() => {
            document.removeEventListener('visibilitychange', updateSubscriptionDependingOnDocumentVisibility);
            worker.close();
        });
    }

    return {
        startUpdating: updateSubscriptionDependingOnDocumentVisibility,
        updateNotifications: requestNotificationsUpdate,
    };
}

function usePolling(notificationsRecord: Ref<NotificationsResource | undefined>) {
    async function fetchNotifications() {
        if (import.meta.server) {
            notificationsRecord.value = await NavbarContext.getNotifications();
        } else {
            try {
                notificationsRecord.value = await NavbarContext.getNotifications();
            } catch (e) {
                if (!(e instanceof FetchError && e.statusCode && [401, 403].includes(e.statusCode))) {
                    /* eslint-disable no-console */
                    console.error('Failed to fetch notifications with error: ', e);
                }
            }
        }
    }

    let fetchInterval: ReturnType<typeof setInterval> | null;

    function setFetchIntervalIfNotAlreadyDone() {
        if (!fetchInterval) {
            fetchInterval = setInterval(fetchNotifications, EVERY_MINUTE);
        }
    }

    function clearFetchIntervalIfNeeded() {
        if (fetchInterval) {
            clearInterval(fetchInterval);
            fetchInterval = null;
        }
    }

    function setOrClearFetchIntervalDependingOnDocumentVisibility() {
        const documentVisible = document.visibilityState === 'visible';
        if (documentVisible) {
            // noinspection JSIgnoredPromiseFromCall
            fetchNotifications();
            setFetchIntervalIfNotAlreadyDone();
        } else {
            clearFetchIntervalIfNeeded();
        }
    }

    if (getCurrentInstance()) {
        onMounted(() => {
            document.addEventListener('visibilitychange', setOrClearFetchIntervalDependingOnDocumentVisibility);
        });
        onBeforeUnmount(() => {
            document.removeEventListener('visibilitychange', setOrClearFetchIntervalDependingOnDocumentVisibility);
            clearFetchIntervalIfNeeded();
        });
    }

    return {
        startUpdating: setOrClearFetchIntervalDependingOnDocumentVisibility,
        updateNotifications: fetchNotifications,
    };
}

export const useNotificationsStore = defineStore('navbar-notifications-store', () => {
    const notificationsRecord = ref<NotificationsResource>();

    const isClientSide = import.meta.client;
    const userAgent = isClientSide ? navigator.userAgent : undefined;
    // Even though the latest Safari versions (16.x.y at the time of writing) now officially support
    // Shared Workers, we observed many cases where the Shared Worker is restarted (apparently, still under
    // investigation). It may be linked to the OS used also.
    const isSafari = userAgent && userAgent.includes('Safari/') && !userAgent.includes('Chrome/') && !userAgent.includes('Chromium/');

    // Compability with Safari 15.6 is checked
    // eslint-disable-next-line compat/compat
    const shouldUseSseSharedWorker = isClientSide && typeof window.SharedWorker !== 'undefined' && !isSafari;

    let updater: ReturnType<typeof useSseSharedWorker> | ReturnType<typeof usePolling> | undefined;

    function getUpdater() {
        if (!updater) {
            updater = shouldUseSseSharedWorker ? useSseSharedWorker(notificationsRecord) : usePolling(notificationsRecord);
        }
        return updater;
    }

    function internalStartUpdating() {
        return getUpdater().startUpdating();
    }

    function internalUpdateNotifications() {
        const updateNotifications = getUpdater().updateNotifications;

        if (import.meta.client) {
            window.addEventListener('notification-task-completed', updateNotifications);

            onScopeDispose(() => {
                if (updateNotifications) {
                    window.removeEventListener('notification-task-completed', updateNotifications);
                }
            });
        }

        return updateNotifications();
    }

    return {
        notificationsRecord,
        startUpdating: internalStartUpdating,
        updateNotifications: internalUpdateNotifications,
    };
});
